import { useState, useEffect, useCallback } from 'react';
import { apiCall } from '../services/api';

export interface BatchRecord {
  jobId: string;
  zipFileName: string | null;
  archivedFileCount: number | null;
  totalSize: number | null;
  urlExpiresAt: string | null;
  downloadUrl: string | null;
  completedAt: string | null;
  files: string[];
}

export interface UseBatchesReturn {
  batches: BatchRecord[];
  isLoading: boolean;
  error: string | null;
  refresh: () => void;
  deleteBatch: (jobId: string) => Promise<void>;
}

export function useBatches(bucketName: string): UseBatchesReturn {
  const [batches, setBatches] = useState<BatchRecord[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const fetchBatches = useCallback(async (): Promise<void> => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await apiCall('/s3/my-batches/' + bucketName);
      setBatches(response.batches || []);
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to load batches';
      setError(message);
    } finally {
      setIsLoading(false);
    }
  }, [bucketName]);

  // Fetch on mount and when bucketName changes
  useEffect(() => {
    void fetchBatches();
  }, [fetchBatches]);

  // 60-second expiry tick — forces re-render so expiry labels update without a server call
  useEffect(() => {
    const intervalId = setInterval(() => {
      setBatches(prev => [...prev]); // new array reference triggers re-render
    }, 60_000);
    return () => clearInterval(intervalId);
  }, []);

  const deleteBatch = useCallback(async (jobId: string): Promise<void> => {
    await apiCall('/s3/my-batches/' + bucketName + '/' + encodeURIComponent(jobId), {
      method: 'DELETE',
    });
    // Optimistic removal — no re-fetch
    setBatches(prev => prev.filter(b => b.jobId !== jobId));
  }, [bucketName]);

  const refresh = useCallback((): void => {
    void fetchBatches();
  }, [fetchBatches]);

  return { batches, isLoading, error, refresh, deleteBatch };
}
